import { images } from '../constants/images';
import Sprite from '../sprites/sprite';
import { useAudioVisualiser } from '../hooks/use-audio-visualiser';
import { Trans, useTranslation } from 'react-i18next';

const firstColumn = [
  images['mudhouse-hero'], // Stunning mudhouse exterior
  images['yajna'],
  images['sanskriti-van-hero-bg'],
  images['mudhouse-interior-1'], // Beautiful interior
  images['serenity-and-peace-4'],
];

const secondColumn = [
  images['gallery-5'],
  images['mudhouse-exterior-1'], // Exterior view
  images['mudhouse-interior-2'], // Interior ambiance
];

const thirdColumn = [
  images['meditation'],
  images['mudhouse-dining'],
  images['haven-9'],
  images['mudhouse-detail'], // Architectural detail
];

const columns = [firstColumn, secondColumn, thirdColumn];

export const SanskritiVanSection = () => {
  const { isPlaying, handleAudioPlay, handleAudioEnd, canvasRef } =
    useAudioVisualiser('/sanskriti-theme.mp3');

  const { t } = useTranslation(['home', 'common']);

  return (
    <section
      id="sanskriti"
      className="w-full relative py-20 md:py-28 z-0 bg-gradient-to-b from-emerald-50 via-emerald-100 to-emerald-200"
    >
      {/* Audio Visualizer Canvas */}
      <canvas
        className="absolute top-0 left-0 w-full h-36 -z-10 rotate-180 opacity-50"
        ref={canvasRef}
      />

      {/* Background Image Overlay */}
      <img
        className="absolute left-0 top-0 w-full h-full opacity-10 object-cover z-[-1]"
        src={images['sanskriti-van-hero-bg']}
        alt="The forest ambience of Sanskriti Vann"
      />

      {/* Content Container */}
      <div className="max-w-6xl mx-auto px-6 sm:px-12">
        {/* Header Section */}
        <div className="text-center mb-12 md:mb-16">
          {/* Badge with Audio Player */}
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="px-4 py-1.5 bg-emerald-200/80 backdrop-blur-sm text-emerald-800 rounded-full text-sm font-medium border border-emerald-300/50">
              {t('common:sanskriti-van.title')}
            </span>
            <button
              onClick={isPlaying ? handleAudioEnd : handleAudioPlay}
              className="relative flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-md border border-emerald-200 hover:scale-110 transition-transform group"
              aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
            >
              {isPlaying && (
                <div className="absolute inset-0 rounded-full bg-emerald-400/30 animate-ping" />
              )}
              {isPlaying ? (
                <Sprite name="mute" width="17" height="12" className="text-emerald-700" />
              ) : (
                <Sprite
                  name="unmute"
                  width="16"
                  height="12"
                  className="text-emerald-700"
                />
              )}
            </button>
          </div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-display text-emerald-900 mb-4">
            <Trans
              i18nKey="home:sanskriti-van.unique"
              components={{
                br: <br />,
              }}
            />
          </h2>

          <p className="text-lg md:text-xl text-emerald-800/80 max-w-2xl mx-auto leading-relaxed">
            {t('home:sanskriti-van.location')}
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {columns.map((column, index) => (
            <ul
              key={index}
              className={`flex flex-col gap-4 md:gap-6 ${index === 1 ? 'md:mt-8' : ''}`}
            >
              {column.map((src, key) => (
                <li key={key} className="group">
                  <div className="relative overflow-hidden rounded-xl shadow-lg">
                    <img
                      src={src}
                      className="object-cover w-full max-h-80 md:max-h-[unset] select-none pointer-events-none transform group-hover:scale-105 transition-transform duration-500"
                      alt=""
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </li>
              ))}
            </ul>
          ))}
        </div>
      </div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 w-full left-0 h-24 bg-gradient-to-b from-transparent to-green-100" />
    </section>
  );
};
